import React, { Component } from "react";
import styled from "styled-components";

import { getCanvasBundle } from "js/canvas";
import { appVersion } from "js/config";
import getLogger, { LogGroup } from "js/core/logger";
import { Slide } from "js/core/models/slide";
import { $, _ } from "js/vendor";

const logger = getLogger(LogGroup.CANVAS);

const CanvasFrame = styled.div`
    width: 1280px;
    height: 720px;
    box-shadow: ${props => props.shadow ? "0px 0px 20px rgba(0, 0, 0, .2)" : "none"};
    outline: solid 1px #ccc;
`;

class CanvasPreview extends Component {
    constructor() {
        super();

        this.ref = React.createRef();

        this.renderPromise = Promise.resolve();
        this.touchStartX = null;
        this.touchStartY = null;
    }

    componentDidMount() {
        const { slideModel, width, theme, onRendered, onCanvasRendered, animateOnMount } = this.props;

        this.renderPromise = (async () => {
            const { SlideCanvas } = await getCanvasBundle(appVersion);
            const { CanvasEventType } = await import("common/constants");

            try {
                const model = new Slide(_.cloneDeep(slideModel), { disconnected: true });
                this.slideCanvas = new SlideCanvas({
                    dataModel: model,
                    canvasWidth: 1280,
                    canvasHeight: 720,
                    editable: false,
                    theme
                });
                $(this.ref.current).append(this.slideCanvas.render().$el);

                this.slideCanvas.$el.css({ transform: `scale(${width / 1280})` });

                // Listen for canvas render completion
                if (onCanvasRendered) {
                    this.slideCanvas.once(CanvasEventType.RENDER, () => {
                        onCanvasRendered();
                    });
                }

                await this.slideCanvas.renderSlide();

                onRendered && onRendered();

                // Trigger animation on mount if requested
                if (animateOnMount) {
                    this.animateCanvas();
                }
            } catch (err) {
                logger.error(err, "[CanvasPreview] componentDidMount() renderPromise failed", { slideId: this.props.slideModel?.id });
            }
        })();
    }

    componentDidUpdate(prevProps) {
        const { onRendered } = this.props;

        this.renderPromise = this.renderPromise.then(async () => {
            if (prevProps.theme !== this.props.theme) {
                try {
                    this.slideCanvas.options.theme = this.props.theme;
                    await this.slideCanvas.renderModel();
                    onRendered && onRendered();
                } catch (err) {
                    logger.error(err, "[CanvasPreview] componentDidUpdate() failed", { slideId: this.props.slideModel?.id });
                }
            }

            if (prevProps.slideColor !== this.props.slideColor) {
                this.slideCanvas.model.layout.slideColor = this.props.slideColor;
                this.slideCanvas.refreshCanvas(true);
            }
            if (prevProps.backgroundColor !== this.props.backgroundColor) {
                this.slideCanvas.model.layout.backgroundStyle = this.props.backgroundStyle;
                this.slideCanvas.model.layout.backgroundColor = this.props.backgroundColor;
                this.slideCanvas.refreshCanvas(true);
            }
        });
    }

    animateCanvas = () => {
        const { onAnimationComplete } = this.props;
        this.slideCanvas.prepareForAnimation().then(() => {
            this.slideCanvas.animate(() => {
                onAnimationComplete && onAnimationComplete();
            }, false);
        });
    }

    handleTouchStart = e => {
        this.touchStartX = e.touches[0].clientX;
        this.touchStartY = e.touches[0].clientY;
    }

    handleTouchEnd = e => {
        if (this.touchStartX === null || this.touchStartY === null) {
            return;
        }

        const touchEndX = e.changedTouches[0].clientX;
        const touchEndY = e.changedTouches[0].clientY;

        const deltaX = Math.abs(touchEndX - this.touchStartX);
        const deltaY = Math.abs(touchEndY - this.touchStartY);

        // Only animate if movement is minimal (< 10px) - this was a tap, not a drag
        if (deltaX < 10 && deltaY < 10) {
            this.animateCanvas();
        }

        this.touchStartX = null;
        this.touchStartY = null;
    }

    render() {
        const { position = "absolute", translateX = 0, translateY = 0, scale = 1, transition, onClick, animateOnHover, shadow = true } = this.props;

        let style;
        if (position === "absolute") {
            style = {
                position: "absolute",
                transform: `translateX(${translateX}px) translateY(${translateY}px) scale(${scale})`,
                transformOrigin: "0 0",
                transition: transition ? "300ms" : "none"
            };
        }

        return (
            <CanvasFrame
                ref={this.ref}
                className="canvas-frame"
                shadow={shadow}
                onClick={onClick}
                animateOnHover={animateOnHover}
                {...(animateOnHover ? {
                    onMouseEnter: this.animateCanvas,
                    onTouchStart: this.handleTouchStart,
                    onTouchEnd: this.handleTouchEnd
                } : {})}
                style={style}
            />
        );
    }
}

export default CanvasPreview;
